import * as Sentry from '@sentry/vue'

import { appBranding } from '@/lib/features'

/**
 * @param {Object} deps
 * @param {string[]} deps.availableLocales
 * @param {Set<string>} deps.allNamespaces
 * @param {string[]} deps.overrideNamespaces
 * @param {string|undefined} deps.locizeProjectId
 * @param {(locale: string) => string|string[]} deps.getFallbackLocale
 * @param {(routePath?: string) => string[]} deps.getActiveOverrideNamespaces
 * @param {() => string} deps.getCurrentRoutePath
 */
export function createSentryReporter({
  availableLocales,
  allNamespaces,
  overrideNamespaces,
  locizeProjectId,
  getFallbackLocale,
  getActiveOverrideNamespaces,
  getCurrentRoutePath,
}) {
  /**
   * @param {string} lang
   * @param {Object} [options]
   * @param {string} [options.action]
   * @param {Record<string, Object>|null} [options.namespaceResults]
   */
  function buildI18nSentryContext(lang, { action, namespaceResults = null } = {}) {
    const baseLanguage = lang.includes('-') ? lang.split('-')[0] : lang
    const localLocaleVariants = availableLocales.filter(
      (locale) =>
        locale === lang || locale.startsWith(`${baseLanguage}-`) || locale === baseLanguage
    )

    const emptyNamespaces = []
    const failedNamespaces = []
    const localFallbackNamespaces = []
    const successfulNamespaces = []
    const recoveredFromEmptyNamespaces = []

    if (namespaceResults) {
      Object.entries(namespaceResults).forEach(([namespace, result]) => {
        if (result.status === 'ok') {
          successfulNamespaces.push(namespace)
        } else if (result.status === 'empty_with_local_fallback') {
          recoveredFromEmptyNamespaces.push(namespace)
        } else if (result.status === 'empty') {
          emptyNamespaces.push(namespace)
        } else if (result.status === 'error') {
          failedNamespaces.push(namespace)
        }
        if (result.usedLocalFallback) {
          localFallbackNamespaces.push(namespace)
        }
      })
    }

    return {
      tags: {
        component: 'i18n',
        locale: lang,
        ...(action ? { action } : {}),
      },
      extra: {
        lang,
        action,
        locizeVersion: import.meta.env.VITE_LOCIZE_VERSION || 'production',
        hasLocizeProjectId: Boolean(locizeProjectId),
        availableLocales,
        hasLocalLocaleFiles: availableLocales.includes(lang),
        localLocaleVariants,
        fallbackLocale: getFallbackLocale(lang),
        platform: appBranding?.config?.platform,
        activeOverrideNamespaces: getActiveOverrideNamespaces(getCurrentRoutePath()),
        routePath: getCurrentRoutePath(),
        namespacesRequested: namespaceResults
          ? Object.keys(namespaceResults)
          : [...allNamespaces, ...overrideNamespaces],
        namespaceResults,
        emptyNamespaces,
        failedNamespaces,
        localFallbackNamespaces,
        recoveredFromEmptyNamespaces,
        successfulNamespaces,
      },
    }
  }

  /**
   * @param {'error'|'warning'|'info'} level
   * @param {string} message
   * @param {Object} [context]
   */
  function reportI18nIssue(level, message, context = {}) {
    const { lang, action, namespaceResults = null, ...additionalExtra } = context
    const { tags, extra } = buildI18nSentryContext(lang, { action, namespaceResults })

    Sentry.captureMessage(message, { level, tags, extra: { ...extra, ...additionalExtra } })
  }

  return { buildI18nSentryContext, reportI18nIssue }
}
